/**
 * 🚀 AI-Powered Smart Property Upload
 * Drop PDF/Images → AI Vision extracts everything → Auto-create property!
 * FREE AI API - 100% cost savings! 🎉
 */

import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Loader, Sparkles, X } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const PDFPropertyUpload = ({ tenantId, onPropertyCreated, onClose }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [useAI, setUseAI] = useState(true);
    const [autoSave, setAutoSave] = useState(true);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        setSelectedFiles(files);
        setResults([]);
        setError(null);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        setSelectedFiles(files);
        setResults([]);
        setError(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);
        setError(null);
        setResults([]);

        try {
            const uploadResults = [];

            // Upload files one by one (or batch if needed)
            for (const file of selectedFiles) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('use_ai', useAI);
                formData.append('auto_save', autoSave);

                const response = await fetch(
                    `${API_BASE_URL}/api/tenants/${tenantId}/properties/smart-upload`,
                    {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: formData
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    uploadResults.push({
                        filename: file.name,
                        success: false,
                        error: data.detail || 'Upload failed'
                    });
                } else {
                    uploadResults.push({
                        filename: file.name,
                        success: true,
                        data: data,
                        confidence: data.confidence
                    });

                    // If auto-save created a property, notify parent
                    if (autoSave && data.property_id && onPropertyCreated) {
                        onPropertyCreated({ id: data.property_id });
                    }
                }
            }

            setResults(uploadResults);

        } catch (err) {
            console.error('Upload error:', err);
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="glass-card rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div className="text-center flex-1">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 bg-opacity-20 rounded-full mb-4">
                            <Sparkles className="w-8 h-8 text-purple-400" />
                        </div>
                        <h2 className="text-3xl font-bold text-white mb-2">
                            🚀 AI Smart Upload
                        </h2>
                        <p className="text-gray-400">
                            Drop PDF brochures or images - AI Vision extracts everything!
                        </p>
                        <p className="text-sm text-green-400 mt-1">
                            ✨ FREE AI Analysis - 95% accuracy - 20x faster!
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Drag & Drop Zone */}
                <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="relative mb-6"
                >
                    <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                        disabled={uploading}
                    />
                    <label
                        htmlFor="file-upload"
                        className="flex flex-col items-center justify-center w-full h-48 border-3 border-dashed border-purple-500 border-opacity-50 rounded-xl cursor-pointer hover:border-opacity-100 transition-all bg-navy-light hover:bg-opacity-80"
                    >
                        <Upload className="w-12 h-12 text-purple-400 mb-4" />
                        <p className="text-lg font-semibold text-white mb-2">
                            Drag & Drop or Click to Browse
                        </p>
                        <p className="text-sm text-gray-400">
                            PDF brochures, JPG, PNG, WEBP images
                        </p>
                    </label>
                </div>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                    <div className="mb-6">
                        <h3 className="text-white font-semibold mb-3">
                            Selected Files ({selectedFiles.length}):
                        </h3>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                            {selectedFiles.map((file, index) => (
                                <div key={index} className="flex items-center gap-3 bg-navy-light p-3 rounded-lg">
                                    <FileText className="w-5 h-5 text-purple-400" />
                                    <span className="text-white flex-1 truncate">{file.name}</span>
                                    <span className="text-gray-400 text-sm">{formatFileSize(file.size)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Options */}
                <div className="flex gap-4 mb-6 flex-wrap">
                    <label className="flex items-center gap-2 bg-navy-light px-4 py-2 rounded-lg cursor-pointer">
                        <input
                            type="checkbox"
                            checked={useAI}
                            onChange={(e) => setUseAI(e.target.checked)}
                            className="w-4 h-4"
                        />
                        <span className="text-white">🤖 Use Vision AI for Analysis & Extraction (FREE!)</span>
                    </label>
                    <label className="flex items-center gap-2 bg-navy-light px-4 py-2 rounded-lg cursor-pointer">
                        <input
                            type="checkbox"
                            checked={autoSave}
                            onChange={(e) => setAutoSave(e.target.checked)}
                            className="w-4 h-4"
                        />
                        <span className="text-white">💾 Auto-save Properties (if confidence &gt; 70%)</span>
                    </label>
                </div>

                {/* Upload Button */}
                {selectedFiles.length > 0 && !uploading && (
                    <button
                        onClick={handleUpload}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4 rounded-lg hover:shadow-lg hover:shadow-purple-500/50 transition-all flex items-center justify-center gap-2"
                    >
                        <Sparkles className="w-5 h-5" />
                        Upload & Extract with AI
                    </button>
                )}

                {/* Uploading State */}
                {uploading && (
                    <div className="flex flex-col items-center justify-center py-8">
                        <Loader className="w-12 h-12 text-purple-400 animate-spin mb-4" />
                        <p className="text-white font-semibold">AI is analyzing your document...</p>
                        <p className="text-gray-400 text-sm mt-2">Extracting property details from {selectedFiles.length} file(s)</p>
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="flex items-start gap-3 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-4 mb-4">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-red-500 font-semibold">Upload Failed</p>
                            <p className="text-red-400 text-sm">{error}</p>
                        </div>
                    </div>
                )}

                {/* Results */}
                {results.length > 0 && (
                    <div className="space-y-4">
                        <h3 className="text-white font-bold text-xl mb-4">
                            Results ({results.filter(r => r.success).length} / {results.length} successful)
                        </h3>
                        {results.map((result, index) => (
                            <div
                                key={index}
                                className={`p-4 rounded-lg border ${
                                    result.success
                                        ? 'bg-green-500 bg-opacity-10 border-green-500'
                                        : 'bg-red-500 bg-opacity-10 border-red-500'
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    {result.success ? (
                                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    )}
                                    <div className="flex-1">
                                        <p className={`font-semibold mb-1 ${result.success ? 'text-green-500' : 'text-red-500'}`}>
                                            {result.filename}
                                        </p>
                                        {result.success ? (
                                            <>
                                                <p className="text-green-400 text-sm mb-2">
                                                    ✨ Confidence: {(result.confidence * 100).toFixed(1)}%
                                                </p>
                                                {result.data?.extracted && (
                                                    <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                                                        {result.data.extracted.price && (
                                                            <div>
                                                                <span className="text-gray-400">💰 Price:</span>
                                                                <span className="text-white ml-2">
                                                                    {result.data.extracted.price.toLocaleString()} AED
                                                                </span>
                                                            </div>
                                                        )}
                                                        {result.data.extracted.bedrooms && (
                                                            <div>
                                                                <span className="text-gray-400">🛏️ Bedrooms:</span>
                                                                <span className="text-white ml-2">{result.data.extracted.bedrooms}</span>
                                                            </div>
                                                        )}
                                                        {result.data.extracted.area_sqft && (
                                                            <div>
                                                                <span className="text-gray-400">📏 Area:</span>
                                                                <span className="text-white ml-2">{result.data.extracted.area_sqft} sqft</span>
                                                            </div>
                                                        )}
                                                        {result.data.extracted.location && (
                                                            <div>
                                                                <span className="text-gray-400">📍 Location:</span>
                                                                <span className="text-white ml-2">{result.data.extracted.location}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {autoSave && result.data?.property_id && (
                                                    <p className="text-green-300 text-sm mt-2">
                                                        ✅ Auto-saved as Property #{result.data.property_id}
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <p className="text-red-400 text-sm">{result.error}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setResults([]);
                                    setSelectedFiles([]);
                                    setError(null);
                                }}
                                className="flex-1 bg-purple-500 text-white font-semibold py-3 rounded-lg hover:bg-purple-400 transition-all"
                            >
                                Upload More
                            </button>
                            <button
                                onClick={onClose}
                                className="flex-1 bg-navy-light text-white font-semibold py-3 rounded-lg hover:bg-opacity-80 transition-all"
                            >
                                Close & Refresh
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PDFPropertyUpload;
